import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { ImagePlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AreaInput, EditorShell, Field, PanelShell, RowActions, SelectInput, TextInput, del, useCrud } from "@/components/admin/ui";
import { GalleryUpload, SingleImageUpload } from "@/components/admin/ImageUpload";

const CAT_OPTIONS = [
  { value: "concert", label: "演唱会" },
  { value: "festival", label: "音乐节" },
  { value: "corporate", label: "商业活动" },
  { value: "production", label: "音乐制作" },
];
const CAT_LABEL = { concert: "演唱会", festival: "音乐节", corporate: "商业活动", production: "音乐制作" };

const emptyProject = { title: "", year: 2025, location: "", category: "concert", role: "", image: "", featured: false, lineup: "", details: "", gallery: [] };

export const ProjectsPanel = () => {
  const { items, load } = useCrud("projects");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const coverInput = useRef(null);
  const replacingId = useRef(null);
  const [savingCover, setSavingCover] = useState(false);
  const [dragHome, setDragHome] = useState(null);
  useEffect(() => {
    load();
  }, []);

  const quickCover = async (file) => {
    const id = replacingId.current;
    if (!file || !id) return;
    setSavingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      const item = items.find((x) => x.id === id);
      await api.put(`/projects/${id}`, { ...item, image: url });
      toast.success("封面已更换，官网即时生效");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingCover(false);
      replacingId.current = null;
    }
  };

  const homeFeatured = items
    .filter((p) => p.home_featured)
    .sort((a, b) => (a.home_order ?? 9999) - (b.home_order ?? 9999));

  const reorderHome = async (from, to) => {
    if (from === to) return;
    const nv = [...homeFeatured];
    const [moved] = nv.splice(from, 1);
    nv.splice(to, 0, moved);
    try {
      await Promise.all(nv.map((p, i) => api.put(`/projects/${p.id}`, { ...p, home_order: i })));
      toast.success("首页精选顺序已保存");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      if (form.id) await api.put(`/projects/${form.id}`, form);
      else await api.post("/projects", form);
      toast.success("已保存");
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelShell title="演出项目" onAdd={() => setForm({ ...emptyProject })}>
      <div className="space-y-2" data-testid="admin-projects-list">
        {items.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-4 border border-white/10 bg-[#0a0a0a] px-4 py-3">
            <div className="flex items-center gap-4 min-w-0">
              {p.image && <img src={p.image} alt="" className="w-12 h-12 object-cover border border-white/10 shrink-0" />}
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono2 text-xs text-[#ffde00]">{p.year || "—"}</span>
                  <span className="font-zh text-[10px] text-[#a1a1aa] border border-white/15 px-1.5 py-0.5">{CAT_LABEL[p.category]}</span>
                  {p.home_featured && <span className="font-mono2 text-[9px] px-1.5 py-0.5 bg-white/10 text-[#a1a1aa]">首页精选</span>}
                  {p.featured && <span className="font-mono2 text-[9px] text-black bg-[#ffde00] px-1.5 py-0.5">大卡</span>}
                </div>
                <div className="font-zh text-sm text-white truncate mt-1">{p.title}</div>
                <div className="font-mono2 text-[10px] text-[#52525b]">{p.location}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input
                ref={coverInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => quickCover(e.target.files?.[0])}
                data-testid="admin-quick-cover-input"
              />
              <button
                onClick={() => {
                  replacingId.current = p.id;
                  coverInput.current?.click();
                }}
                disabled={savingCover}
                className="btn-ghost !py-1.5 !px-3 !text-[10px] disabled:opacity-50"
                data-testid={`admin-quick-cover-${p.id}`}
              >
                <ImagePlus size={12} /> 换图
              </button>
              <RowActions
                onEdit={() => setForm(p)}
                onDelete={async () => {
                  if (await del("projects", p.id, p.title)) load();
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {homeFeatured.length > 1 && (
        <div className="mt-8 border border-white/10 bg-[#0a0a0a] p-5">
          <div className="font-zh text-sm text-white mb-1">首页精选排序</div>
          <p className="font-zh text-[10px] text-[#52525b] mb-3">拖动调整顺序，保存后官网「精选演出」按此顺序展示</p>
          <div className="space-y-2" data-testid="admin-home-order-list">
            {homeFeatured.map((p, i) => (
              <div
                key={p.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", String(i));
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
                  if (!isNaN(from)) reorderHome(from, i);
                }}
                className={`flex items-center gap-3 border border-white/10 bg-black px-3 py-2 cursor-grab active:cursor-grabbing ${dragHome === i ? "opacity-50" : ""}`}
                data-testid={`admin-home-order-item-${i}`}
              >
                <span className="font-mono2 text-xs text-[#ffde00] w-6">{String(i + 1).padStart(2, "0")}</span>
                {p.image && <img src={p.image} alt="" className="w-9 h-9 object-cover" />}
                <span className="font-zh text-xs text-white truncate flex-1">{p.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {form && (
        <EditorShell title={form.id ? "编辑项目" : "新增项目"} onClose={() => setForm(null)} onSubmit={save} busy={busy}>
          <Field label="标题 TITLE">
            <TextInput required data-testid="admin-project-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="年份 YEAR">
              <TextInput type="number" value={form.year ?? ""} onChange={(e) => setForm({ ...form, year: e.target.value ? parseInt(e.target.value) : null })} />
            </Field>
            <Field label="地点 LOCATION">
              <TextInput value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4 items-end">
            <Field label="分类 CATEGORY">
              <SelectInput options={CAT_OPTIONS} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </Field>
            <div className="flex flex-col gap-2 pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#ffde00]" data-testid="admin-project-featured" />
                <span className="font-zh text-sm text-[#a1a1aa]">作品页大卡</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.home_featured} onChange={(e) => setForm({ ...form, home_featured: e.target.checked })} className="accent-[#ffde00]" data-testid="admin-project-home" />
                <span className="font-zh text-sm text-[#a1a1aa]">首页精选</span>
              </label>
            </div>
          </div>
          <Field label="角色 ROLE">
            <TextInput value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Artist Management / Produce / Production by DFE" />
          </Field>
          <Field label="阵容 LINEUP">
            <AreaInput rows={2} value={form.lineup} onChange={(e) => setForm({ ...form, lineup: e.target.value })} placeholder="BLACK EYED PEAS / will.i.am / apl.de.ap / Taboo…" />
          </Field>
          <Field label="制作说明 DETAILS">
            <AreaInput rows={4} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="演出制作说明：场地、规模、制作范围…" />
          </Field>
          <Field label="封面图 COVER IMAGE">
            <SingleImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} testid="admin-project-cover" />
          </Field>
          <Field label="现场图集 GALLERY">
            <GalleryUpload
              values={form.gallery || []}
              onAdd={(url) => setForm({ ...form, gallery: [...(form.gallery || []), url] })}
              onRemove={(i) => setForm({ ...form, gallery: form.gallery.filter((_, idx) => idx !== i) })}
              onReorder={(nv) => setForm({ ...form, gallery: nv })}
            />
          </Field>
          <Field label="图片 URL（备用）">
            <TextInput value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://…" />
          </Field>
        </EditorShell>
      )}
    </PanelShell>
  );
};

export const ServicesPanel = () => {
  const { items, load } = useCrud("services");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (form.id) await api.put(`/services/${form.id}`, form);
      else await api.post("/services", { ...form, order: items.length });
      toast.success("已保存");
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelShell title="服务项目" onAdd={() => setForm({ title_en: "", title_zh: "", description: "", order: items.length })}>
      <div className="space-y-2" data-testid="admin-services-list">
        {items.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-4 border border-white/10 bg-[#0a0a0a] px-4 py-3">
            <div className="min-w-0">
              <div className="font-zh text-sm text-white">{s.title_zh || s.title_en}</div>
              <div className="font-mono2 text-[10px] text-[#52525b] uppercase truncate">{s.title_en}</div>
            </div>
            <RowActions
              onEdit={() => setForm(s)}
              onDelete={async () => {
                if (await del("services", s.id, s.title_en)) load();
              }}
            />
          </div>
        ))}
      </div>
      {form && (
        <EditorShell title={form.id ? "编辑服务" : "新增服务"} onClose={() => setForm(null)} onSubmit={save} busy={busy}>
          <Field label="中文名称">
            <TextInput required value={form.title_zh} onChange={(e) => setForm({ ...form, title_zh: e.target.value })} />
          </Field>
          <Field label="英文名称">
            <TextInput required value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} />
          </Field>
          <Field label="描述（中文）">
            <AreaInput rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <Field label="描述（英文 Description EN）">
            <AreaInput rows={3} value={form.description_en || ""} onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
          </Field>
        </EditorShell>
      )}
    </PanelShell>
  );
};

export const TeamPanel = () => {
  const { items, load } = useCrud("team");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (form.id) await api.put(`/team/${form.id}`, form);
      else await api.post("/team", { ...form, order: items.length });
      toast.success("已保存");
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelShell title="团队成员" onAdd={() => setForm({ name: "", role_en: "", role_zh: "", bio: "", image: "", order: items.length })}>
      <div className="space-y-2" data-testid="admin-team-list">
        {items.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-4 border border-white/10 bg-[#0a0a0a] px-4 py-3">
            <div className="min-w-0">
              <div className="font-zh text-sm text-white">{m.name}</div>
              <div className="font-mono2 text-[10px] text-[#52525b] truncate">{m.role_en}</div>
            </div>
            <RowActions
              onEdit={() => setForm(m)}
              onDelete={async () => {
                if (await del("team", m.id, m.name)) load();
              }}
            />
          </div>
        ))}
      </div>
      {form && (
        <EditorShell title={form.id ? "编辑成员" : "新增成员"} onClose={() => setForm(null)} onSubmit={save} busy={busy}>
          <Field label="姓名 NAME">
            <TextInput required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="职位（中文）">
              <TextInput value={form.role_zh} onChange={(e) => setForm({ ...form, role_zh: e.target.value })} />
            </Field>
            <Field label="职位（英文）">
              <TextInput value={form.role_en} onChange={(e) => setForm({ ...form, role_en: e.target.value })} />
            </Field>
          </div>
          <Field label="简介 BIO（中文）">
            <AreaInput rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          </Field>
          <Field label="简介 BIO（英文）">
            <AreaInput rows={4} value={form.bio_en || ""} onChange={(e) => setForm({ ...form, bio_en: e.target.value })} />
          </Field>
          <Field label="照片 PHOTO">
            <SingleImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} testid="admin-team-photo" />
          </Field>
        </EditorShell>
      )}
    </PanelShell>
  );
};
